import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import Hero from "@/components/Hero";
import { getCourses } from "@/lib/courses";
import { getUserCourses } from "@/lib/user-courses";
import { useAuth } from "@/contexts/AuthContext";
import CoursesBenefits from "@/components/courses/CoursesBenefits";
import CoursesGrid from "@/components/courses/CoursesGrid";
import CoursesCTA from "@/components/courses/CoursesCTA";

const Courses = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const coursesPerPage = 6;
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  // Store the set of course IDs in which the user is enrolled
  const [enrolledCourseIds, setEnrolledCourseIds] = useState<Set<string>>(new Set());

  // Fetch enrolled courses when user logs in
  useEffect(() => {
    if (user) {
      getUserCourses(user.id)
        .then(userCourses => {
          setEnrolledCourseIds(
            new Set(userCourses.map(enrollment => enrollment.course_id))
          );
        })
        .catch(() => {
          setEnrolledCourseIds(new Set());
        });
    } else {
      setEnrolledCourseIds(new Set());
    }
  }, [user]);

  // Fetch only published courses from Supabase
  const { 
    data: courses = [], 
    isLoading, 
    error 
  } = useQuery({
    queryKey: ['courses', 'published'],
    queryFn: async () => {
      const allCourses = await getCourses();
      // Only include published courses
      return allCourses.filter((c: any) => c.status?.toLowerCase() === "published");
    }
  });

  // Calculate pagination
  const totalPages = Math.ceil(courses.length / coursesPerPage);
  const startIndex = (currentPage - 1) * coursesPerPage;
  const endIndex = startIndex + coursesPerPage;
  const currentCourses = courses.slice(startIndex, endIndex);

  // Handle page changes
  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Handle clicking on a course card
  const handleCourseClick = (courseId: string, slug?: string) => {
    if (authLoading) return;

    // If user is logged in and enrolled, go to learner page
    if (user && enrolledCourseIds.has(courseId)) {
      navigate(`/learner/course/${courseId}`);
      return;
    }

    // Always navigate to the course detail page first
    if (slug) {
      navigate(`/course/slug/${slug}`);
    } else {
      navigate(`/course/${courseId}`);
    }
  };

  // Generate page numbers for pagination
  const pageNumbers = [];
  for (let i = 1; i <= totalPages; i++) {
    pageNumbers.push(i);
  }

  return (
    <div className="fade-in">
      {/* Hero Section */}
      <Hero 
        title="Videokursused"
        subtitle="Õpi oma tempos ja kohas läbi põhjalike videokursuste, mis annavad sulle praktilised oskused ja teadmised."
        bgColor="bg-gradient-to-r from-kaija-light to-kaija-sage"
      />

      {/* Courses Grid */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-medium mb-6">Meie kursused</h2>
            <p className="text-gray-700">
              Vali endale sobiv kursus ja alusta oma õppimist kohe. Kõik kursused sisaldavad videomaterjale, harjutusi ja lisamaterjale.
            </p>
          </div>
          <CoursesGrid 
            isLoading={isLoading}
            error={error}
            courses={courses}
            currentCourses={currentCourses}
            currentPage={currentPage}
            totalPages={totalPages}
            pageNumbers={pageNumbers}
            handlePageChange={handlePageChange}
            handleCourseClick={handleCourseClick}
          />
        </div>
      </section>

      <CoursesBenefits />

      <CoursesCTA />
    </div>
  );
};

export default Courses;
